import { defineComponent as _defineComponent } from 'vue'
import DataRow from 'ah-common-lib/src/common/components/DataRow.vue';
import TradeFundForm from '../../forms/tradeFundForm/TradeFundForm.vue';
import { ClientRiskProfileReport, FundingType, QuotePriceResponse, TradeFundingDetails } from 'ah-api-gateways';
import { VCol, VRow } from 'ah-common-lib/src/common/components';
import { computed, watch, PropType, ref } from 'vue';
import { formatCurrencyValue } from 'ah-common-lib/src/helpers/currency';
import { useOnBehalfOf } from 'ah-common-lib/src/onBehalfOf/useInjectedOBO';
import { useAhTradesState } from '../../..';


export default /*#__PURE__*/_defineComponent({
  __name: 'TradePriceMarginInfo',
  props: {
  tradePriceResponse: {
    type: Object as PropType<QuotePriceResponse>,
    required: true,
  },
  tradeFundingDetails: {
    type: Object as PropType<Partial<TradeFundingDetails>>,
  },
  hideFundingForm: {
    type: Boolean,
    default: false,
  },
},
  emits: ["update:areIMLimitOrCollateralProfileInvalid", "update:sufficientCollateral", "update:tradeFundingDetails"],
  setup(__props, { emit }: { emit: ({
  (e: 'update:areIMLimitOrCollateralProfileInvalid', value: boolean): void;
  (e: 'update:sufficientCollateral', value: boolean): void;
  (e: 'update:tradeFundingDetails', value: Partial<TradeFundingDetails>): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const tradeState = useAhTradesState();

const onBehalfOfClient = useOnBehalfOf();





const clientId = computed(
  () => onBehalfOfClient.value?.id ?? tradeState.store.useAuthStore().loggedInIdentity?.client?.id
);

const clientRiskProfileReportData = computed<ClientRiskProfileReport | null>(() =>
  clientId.value ? tradeState.store.useWalletsStore().getClientRiskProfile(clientId.value) : null
);

const areClientRiskProfileLoading = computed(() => {
  return tradeState.store.useWalletsStore().areClientRiskProfileLoading(clientId.value);
});

const shouldHideIMFundingForm = computed(() => props.hideFundingForm !== false || areClientRiskProfileLoading.value);

const sufficientCollateral = computed(
  () =>
    (!!clientRiskProfileReportData.value?.collateralProfile &&
      clientRiskProfileReportData.value.collateralProfile.availableCollateral >=
        props.tradePriceResponse.homeCurrencyInitialMarginAmount) ||
    props.tradePriceResponse.homeCurrencyInitialMarginAmount <= 0 ||
    isFundWithCreditPossible.value
);

function loadClientRiskProfileReport() {
  // FeatureFlag.VARIATION_MARGIN_AND_LIMITS_FEATURE is assumed to be active, as this component only displays if it is
  if (!clientId.value) {
    throw 'Cannot load client risk profile report with incomplete data';
  }

  tradeState.store.useWalletsStore().loadClientRiskProfile({ force: true, clientId: clientId.value });
}

const isFundWithCollateralPossible = computed(() => {
  if (!clientRiskProfileReportData.value?.collateralProfile || areClientRiskProfileLoading.value) {
    return false;
  }

  return (
    sufficientCollateral.value &&
    (!clientRiskProfileReportData.value?.creditProfile ||
      clientRiskProfileReportData.value.creditProfile?.marginCreditLimitTotal === 0)
  );
});

const isFundWithCreditPossible = computed(() => {
  if (!clientRiskProfileReportData.value?.creditProfile || areClientRiskProfileLoading.value) {
    return false;
  }

  return (
    props.tradePriceResponse.homeCurrencyInitialMarginAmount > 0 &&
    clientRiskProfileReportData.value.creditProfile.marginCreditLimitTotalRemaining >=
      props.tradePriceResponse.homeCurrencyInitialMarginAmount
  );
});

const isFormValid = computed(
  () =>
    isFundWithCollateralPossible.value ||
    isFundWithCreditPossible.value ||
    props.tradePriceResponse.homeCurrencyInitialMarginAmount <= 0 ||
    false
);

const isIMTotalLimitGreaterThanZero = computed(
  () =>
    clientRiskProfileReportData.value?.creditProfile &&
    clientRiskProfileReportData.value.creditProfile.marginCreditLimitTotal > 0
);

const areIMLimitOrCollateralProfileInvalid = computed(
  () =>
    (!clientRiskProfileReportData.value?.collateralProfile && !areClientRiskProfileLoading.value) ||
    (!isFundWithCreditPossible.value && isIMTotalLimitGreaterThanZero.value)
);

const fundType = ref();

function emitValues(creditContribution?: number, collateralContribution?: number) {
  emit('update:tradeFundingDetails', {
    initialMarginFundingType: fundType.value,
    initialMarginFundingCurrency: props.tradePriceResponse.homeCurrency,
    initialMarginCollateralFundingAmount: collateralContribution,
    initialMarginCreditFundingAmount: creditContribution,
    valid: isFormValid.value,
  });
}

/**
 * Initial fund type set
 */
function setFundTypeAndEmit() {
  if (props.tradePriceResponse.homeCurrencyInitialMarginAmount <= 0) {
    emit('update:tradeFundingDetails', {});
  } else if (!isFundWithCreditPossible.value) {
    fundType.value = FundingType.COLLATERAL;
    emitValues(0, props.tradePriceResponse.homeCurrencyInitialMarginAmount);
  } else {
    fundType.value = FundingType.CREDIT;
    emitValues(props.tradePriceResponse.homeCurrencyInitialMarginAmount, 0);
  }
}

function reloadCollaterals() {
  loadClientRiskProfileReport();
  setFundTypeAndEmit();
}

watch(isFundWithCreditPossible, setFundTypeAndEmit, { immediate: true });

watch(isFormValid, () => {
  if (props.tradeFundingDetails) {
    emit('update:tradeFundingDetails', { ...props.tradeFundingDetails, valid: isFormValid.value });
  }
});

watch(() => props.tradePriceResponse, loadClientRiskProfileReport, { immediate: true });

watch(
  sufficientCollateral,
  () => {
    emit('update:sufficientCollateral', sufficientCollateral.value);
  },
  { immediate: true }
);

watch(
  areIMLimitOrCollateralProfileInvalid,
  () => {
    emit('update:areIMLimitOrCollateralProfileInvalid', !!areIMLimitOrCollateralProfileInvalid.value);
  },
  { immediate: true }
);

watch(
  () => props.tradeFundingDetails,
  () => {
    if (props.tradeFundingDetails?.initialMarginFundingType) {
      fundType.value = props.tradeFundingDetails.initialMarginFundingType;
    }
  },
  { immediate: true }
);

return { __sfc: true,tradeState, onBehalfOfClient, emit, props, clientId, clientRiskProfileReportData, areClientRiskProfileLoading, shouldHideIMFundingForm, sufficientCollateral, loadClientRiskProfileReport, isFundWithCollateralPossible, isFundWithCreditPossible, isFormValid, isIMTotalLimitGreaterThanZero, areIMLimitOrCollateralProfileInvalid, fundType, emitValues, setFundTypeAndEmit, reloadCollaterals, DataRow, TradeFundForm, VCol, VRow, formatCurrencyValue }
}

})